import { useState, useMemo, useEffect } from "react";
import { useTenantDashboard, useScriptDashboardOverview } from "@/hooks/useTenantDashboardService";
import { useMeetingDashboardData } from "@/hooks/useMeetingDashboardData";
// import useAuthStore from "@/store/useAuthStore";
import AllRepsPage from "./AllRepsPage";
import AllActivityPage from "./AllActivityPage";
import SkillReportPage from "./SkillReportPage";
import RepDashboardPage from "./RepDashboardPage";
import AllMeetingsPage from "./AllMeetingsPage";
import AllPracticeSessionsPage from "./AllPracticeSessionsPage";
import AllScriptsPage from "./AllScriptsPage";
import { getTeamCoachingOverview, generateTeamCoachingInsights } from "@/api/tenantDashboardService";
import { isAdminRole, isManagerRole } from "@/utils/authStorage";
import TeamCoachingInsightsCard from "@/components/Cards/TeamCoachingInsightsCard";
import TeamCoachingModal from "@/components/Cards/TeamCoachingModal";
import { useLocation, useNavigate } from "react-router-dom";
import { useDashboardTheme, getBadgeTheme } from "./themeHelper";
import { isMeetingRecorded } from "@/utils/meetingFilter";
// ─── Shared Styles ───────────────────────────────────────────────────────────
const getDynamicStyles = (colors: any, isDark: boolean) => ({
  root: {
    minHeight: "100vh",
    background: colors.bg,
    color: colors.text,
    fontFamily: "'Inter', -apple-system, sans-serif",
  },
  main: { padding: "28px 32px", minHeight: "100vh" },
  header: {
    display: "flex", justifyContent: "end", alignItems: "flex-end",
    marginBottom: 28,
  },
  section: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 16, padding: "20px 24px",
  },
  sectionTitle: {
    fontSize: 14, fontWeight: 700, color: colors.text,
    letterSpacing: 1.2, textTransform: "uppercase" as const, marginBottom: 16,
  },
  viewAll: {
    fontSize: 12, color: isDark ? "#A78BFA" : "#5B21B6", cursor: "pointer",
    fontWeight: 600, display: "flex", alignItems: "center", gap: 4,
    background: colors.surfaceAlt,
    border: `1px solid ${colors.border}`,
    borderRadius: 16, padding: "8px 12px",
    transition: "all 0.2s",
    marginBottom: 16,
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────
function avg(arr: number[]) {
  const valid = arr.filter((v) => typeof v === "number" && !isNaN(v) && v > 0); // ✅ added v > 0
  return valid.length ? valid.reduce((a, b) => a + b, 0) / valid.length : 0;
}
function fmtDate(iso: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function getInitials(name = "") {
  const str = String(name || "");
  return str.trim().split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
}
const AVATAR_COLORS = ["#6C63FF", "#FF6584", "#43C6AC", "#F7971E", "#56CCF2", "#BB6BD9", "#F2994A", "#27AE60"];
function avatarColor(name = "") {
  let hash = 0;
  const str = String(name || "");
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function Avatar({ name, size = 36 }: { name: string; size?: number }) {
  const bg = avatarColor(name);
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.35, fontWeight: 700, color: "#fff", flexShrink: 0,
      letterSpacing: 0.5,
    }}>
      {getInitials(name)}
    </div>
  );
}

function getScoreColor(score: number): string {
  if (score >= 90) return "#4ADE80";
  if (score >= 70) return "#A3E635";
  if (score >= 50) return "#FACC15";
  return "#F87171";
}

function ScoreRing({ score, size = 64 }: { score: number; size?: number }) {
  const { colors } = useDashboardTheme();
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(Math.max(score, 0), 100);
  const color = getScoreColor(pct);
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={colors.ringTrack} strokeWidth={5} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={5}
        strokeDasharray={circ} strokeDashoffset={circ - (pct / 100) * circ}
        strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      <text x={size / 2} y={size / 2 + 5} textAnchor="middle" fill={colors.text}
        fontSize={size * 0.26} fontWeight={600}
        style={{ transform: `rotate(90deg) translate(0,-${size}px)`, }}>
        {Math.round(pct)}
      </text>
    </svg>
  );
}

function SkillBar({ label, value, icon }: { label: string; value: number; icon: string }) {
  const { colors, isDark } = useDashboardTheme();
  const pct = Math.min(value, 100);
  const color = getScoreColor(pct);
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {/* Icon */}
        <span style={{ fontSize: 15, opacity: isDark ? 0.7 : 1, flexShrink: 0, filter: isDark ? "grayscale(1) brightness(2)" : "none" }}>
          {icon}
        </span>
        {/* Skill name */}
        <span style={{ fontSize: 15, color: colors.text, fontWeight: 400, minWidth: 130, flexShrink: 0 }}>
          {label}
        </span>
        {/* Bar */}
        <div style={{ marginLeft: 8, flex: 1, height: 8, background: colors.ringTrack, borderRadius: 3, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.7s ease" }} />
        </div>
        {/* Score */}
        <span style={{ fontSize: 15, fontWeight: 600, color: colors.text, minWidth: 24, textAlign: "right" as const }}>
          {Math.round(value)}
        </span>
      </div>
      <div style={{ height: 1, background: colors.divider }} />
    </>

  );
}
function Badge({ type }: { type: string }) {
  const { isDark } = useDashboardTheme();
  const s = getBadgeTheme(type, isDark);
  return (
    <span style={{
      background: s.bg, color: s.color, fontSize: 11, fontWeight: 600,
      padding: "3px 10px", borderRadius: 20, whiteSpace: "nowrap" as const, letterSpacing: 0.3,
    }}>{s.label}</span>
  );
}

function ArrowBtn({ onClick }: { onClick?: () => void }) {
  const [hov, setHov] = useState(false);
  const { isDark } = useDashboardTheme();
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 28, height: 28, borderRadius: 8,
        background: hov 
          ? (isDark ? "rgba(108,99,255,0.3)" : "rgba(108,99,255,0.2)") 
          : (isDark ? "rgba(108,99,255,0.12)" : "rgba(108,99,255,0.06)"),
        border: "1px solid rgba(108,99,255,0.25)",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", flexShrink: 0, transition: "all 0.2s",
        color: isDark ? "#A78BFA" : "#5B21B6", fontSize: 14, fontWeight: 700,
      }}
    >›</button>
  );
}

// ─── Derived Data ─────────────────────────────────────────────────────────────
function deriveMetrics(userData: any, rawMeetings: any[], scriptOverview?: any) {
  const d = userData?.data || userData || {};
  const pitches = Array.isArray(d.tenantPitchMeta?.items)
    ? d.tenantPitchMeta.items
    : Array.isArray(d.tenantPitchMeta)
      ? d.tenantPitchMeta
      : [];
  const scenarios = Array.isArray(d.tenantScenarios) ? d.tenantScenarios : [];
  const userSummary = Array.isArray(d.userPitchSummary)
    ? d.userPitchSummary
    : d.userPitchSummary ? Object.values(d.userPitchSummary) : [];
  const meetings = (Array.isArray(rawMeetings) ? rawMeetings : []).filter(isMeetingRecorded);

  const toNumber = (value: any) => {
    if (typeof value === "number" && !isNaN(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
      const n = Number(value);
      return Number.isFinite(n) ? n : 0;
    }
    if (typeof value === "object" && value !== null) {
      const c =
        value.overallPercentage ??
        value.overallScore ??
        value.score ??
        value.avargerscroing ??
        value.scoreDetails?.overallScore ??
        value.ai_score?.overallScore ??
        value.ai_score?.overall_percentange ??
        value.ai_score?.overall_percentage ??
        value.overall_percentange ??
        value.overall_percentage;
      if (typeof c === "number" && c > 0) return c;
      if (typeof c === "string" && !isNaN(Number(c))) return Number(c);
      if (typeof c === "object" && c !== null) {
        const nested = c.overall_percentange ?? c.overall_percentage ?? c.overallScore ?? c.score;
        if (typeof nested === "number" && nested > 0) return nested;
        if (typeof nested === "string" && !isNaN(Number(nested))) return Number(nested);
      }
    }
    return 0;
  };

  const scoredItems = [
    ...pitches.map((p: any) => toNumber(p.overallPercentage || p.score || p)),
    ...meetings.map((m: any) => toNumber(m.overallPercentage || m.score || m)),
    ...scenarios.map((s: any) => toNumber(s.overallScore || s.score || s)),
  ].filter((value) => value > 0);

  const teamReadiness = scoredItems.length
    ? Math.round(avg(scoredItems))
    : 0;
  const practiceSessions = (toNumber(d.totalScenarioCount) || 0) + (toNumber(d.totalPitchCount) || 0);
  const meetingsReviewed = Array.isArray(rawMeetings) ? meetings.length : (toNumber(d.totalMeetingCount) || 0);

  const scriptSummary = scriptOverview?.summary;
  const scriptAssigned = scriptSummary?.totalAssignments ?? (toNumber(d.totalScriptTotal) || 0);
  const scriptCompleted = scriptSummary?.totalCompletedAssignments ?? (toNumber(d.totalScriptCount) || 0);
  const scriptPracticeCount = scriptSummary?.totalPracticeAttempts ?? 0;
  const scriptsPct = scriptSummary?.completionPercentage ?? (scriptAssigned > 0 ? Math.round((scriptCompleted / scriptAssigned) * 100) : 0);

  const pitchesWithScores = pitches.filter((p: any) => p.categoryScores?.scores);
  const skillAvg = (extractor: (p: any) => number) => {
    const vals = pitchesWithScores.map(extractor).filter((v: number) => typeof v === "number");
    return vals.length ? Math.round((avg(vals) / 5) * 100) : 0;
  };
  const skills = {
    Discovery: skillAvg((p) => {
      const disc = p.categoryScores?.scores?.discovery;
      return disc ? avg([disc.identified_needs, disc.listened_actively, disc.confirmed_understanding, disc.asked_relevant_questions]) : NaN;
    }),
    "Objection Handling": skillAvg((p) => p.categoryScores?.scores?.engagement?.handled_objections),
    "Product Knowledge": skillAvg((p) => {
      const pd = p.categoryScores?.scores?.pitch_delivery;
      return pd ? avg([pd.communicated_value, pd.structured_pitch, pd.used_clear_language]) : NaN;
    }),
    Closing: skillAvg((p) => {
      const c = p.categoryScores?.scores?.closing;
      return c ? avg([c.created_urgency, c.asked_for_next_step, c.summarized_key_points]) : NaN;
    }),
  };

  const getUserId = (item: any) =>
    item?.userId ||
    item?.user?.userId ||
    item?.User?.userId ||
    item?.user?.id ||
    item?.User?.id ||
    item?.createdBy ||
    item?.ownerId ||
    item?.user_id ||
    item?.owner_id ||
    "";

  const usersById: Record<string, string> = {};
  (d.tenantUserList?.items || []).forEach((user: any) => {
    const id = String(user?.id || user?.userId || user?.tenantId || "");
    if (!id) return;
    usersById[id] = user?.name || user?.fullName || user?.userName || user?.email || "";
  });

  const getActivityRepName = (item: any) => {
    if (!item) return "—";
    const name =
      item.User?.name ||
      item.user?.name ||
      item.User?.fullName ||
      item.user?.fullName ||
      item.User?.userName ||
      item.user?.userName ||
      item.userName ||
      item.salesRep ||
      item.salesRepName ||
      item.createdByName ||
      item.ownerName ||
      item.User?.email ||
      item.user?.email ||
      item.customerContact?.fullName ||
      item.contact?.name;

    if (name) return name;

    const userId =
      item.User?.id ||
      item.user?.id ||
      item.userId ||
      item.user_id ||
      item.createdBy ||
      item.ownerId ||
      item.owner_id ||
      item.createdById ||
      item.userId;

    if (userId) {
      const id = String(userId);
      if (usersById[id]) return usersById[id];
    }

    return "—";
  };

  const repScoreMap: Record<string, number[]> = {};
  const addRepScore = (item: any, score: number) => {
    const uid = getUserId(item);
    if (!uid || score <= 0) return;
    if (!repScoreMap[uid]) repScoreMap[uid] = [];
    repScoreMap[uid].push(score);
  };

  pitches.forEach((p: any) => {
    addRepScore(p, toNumber(p.overallPercentage || p.score));
  });

  meetings.forEach((m: any) => {
    addRepScore(m, toNumber(m.overallPercentage || m.score));
  });

  scenarios.forEach((s: any) => {
    addRepScore(s, toNumber(s.overallScore));
  });

  const reps = userSummary
    .filter((u: any) => toNumber(u.pitchCount) > 0 || toNumber(u.practiceSessionCount) > 0)
    .map((u: any, idx: number) => {
      const uid = getUserId(u) || u.id || u._id || `rep-${idx}`;
      return {
        ...u,
        userId: uid,
        avgScore: uid && repScoreMap[uid] ? Math.round(avg(repScoreMap[uid])) : 0,
      };
    })
    .sort((a: any, b: any) => b.avgScore - a.avgScore);

  const capitalize = (value: any) => {
    const text = String(value || "").trim();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
  };

  const getMeetingType = (item: any) => item.otherDetails?.meetingType || item.meetingType || item.meeting_type;

  const getMeetingDetails = (item: any, fallback: string) => {
    const meetingType = capitalize(getMeetingType(item));
    if (meetingType) return meetingType;
    const purpose = item.otherDetails?.meetingPurpose;
    const purposeStr = Array.isArray(purpose) ? purpose.join(", ") : (purpose || "");
    return item.TargetCompanyProfile?.companyName || item.companyName || item.customerData?.companyName || purposeStr || fallback;
  };

  const meetingActivity = meetings
    .map((m: any) => ({
      id: m.id || m._id || m.legacyMongoId || m.meetingId || m.sessionId || '',
      raw: m,
      rep: getActivityRepName(m),
      type: "Meeting Uploaded",
      details: getMeetingDetails(m, "Meeting Uploaded"),
      score: toNumber(m.overallPercentage || m.score || m),
      date: m.createdAt,
      duration: m.durationMinutes ? `${m.durationMinutes}m` : "—",
      isScheduled:
        String(m.status || '').toLowerCase() === 'scheduled' ||
        String(m.legacyMongoId || '').startsWith('cal-') ||
        String(m.legacyMongoId || '').startsWith('live-'),
    }))
    .filter((m: any) => {
      if (m.isScheduled && (!m.score || m.score <= 0)) return false;
      if (String(m.raw?.status || '').toLowerCase() === 'scheduled' && (!m.score || m.score <= 0)) return false;
      return true;
    })
    .slice(0, 20);

  const pitchActivity = pitches.slice(0, 20).map((p: any) => ({
    id: p.id || p._id || p.legacyMongoId || p.meetingId || p.sessionId || '',
    raw: p,
    rep: getActivityRepName(p),
    type: /digital\s*twin/i.test(p.pitchTitle || "") ? "Digital Twin" : "Pitch",
    details: getMeetingDetails(p, "Pitch"),
    score: toNumber(p.overallPercentage || p.score || p),
    date: p.createdAt,
    duration: p.durationMinutes ? `${p.durationMinutes}m` : "—",
  }));

  const scenarioActivity = scenarios.map((s: any) => ({
    id: s.id || s._id || s.legacyMongoId || s.meetingId || s.sessionId || '',
    raw: s,
    rep: getActivityRepName(s),
    type: "Role Play",
    details: s.scenario?.industry || "Role Play",
    score: toNumber(s.overallScore || s.score || s),
    date: s.createdAt,
    duration: "—",
  }));

  const scriptActivity = (scriptOverview?.recentPracticeActivities || []).map((sa: any) => {
    const completedSec = (sa.sections || []).filter((sec: any) => sec.isCompleted).length;
    const totalSec = (sa.sections || []).length || 4;
    const score = Math.round((completedSec / totalSec) * 100);
    return {
      id: sa.assignmentId || sa.scriptMetaId || sa.id,
      raw: sa,
      rep: sa.repName || "—",
      type: "Script Activity",
      details: sa.scriptTitle || "Script Practice",
      score: score,
      date: sa.createdAt,
      duration: "—",
      isScript: true,
    };
  });

  const recentActivity = [...meetingActivity, ...pitchActivity, ...scenarioActivity, ...scriptActivity]
    .filter((item: any) => (item.score || 0) > 0)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 8);

  const allUsers = d.tenantUserList?.items || [];
  const activeUsers = allUsers.filter((u: any) => u.status === "active").length;

  const scoredMeetings = meetings.filter((m: any) => (m.overallPercentage || m.score || 0) > 0);
  const avgMeetingScore = scoredMeetings.length
    ? Math.round(avg(scoredMeetings.map((m: any) => m.overallPercentage || m.score)))
    : 0;
  const pendingMeetings = meetings.filter((m: any) => m.recordingStatus === "PENDING" || m.status === "processing").length;

  return {
    teamReadiness, practiceSessions, meetingsReviewed, scriptsCompleted: scriptCompleted, scriptsPct,
    scriptAssigned, scriptPracticeCount,
    skills, reps, recentActivity, activeUsers,
    totalUsers: d.totalUserCount || 0, avgMeetingScore, pendingMeetings,
  };
}

// ─── Top KPI Card ─────────────────────────────────────────────────────────────
function TopCard({ icon, label, value, sub, accent, onClick }: {
  icon: string; label: string; value: string | number; sub?: string; accent: string; onClick?: () => void;
}) {
  const { colors } = useDashboardTheme();
  return (
    <div
      onClick={onClick}
      style={{
        background: colors.cardBg,
        border: `1px solid ${colors.topCardBorder}`,
        borderRadius: 16,
        padding: "16px 18px",
        display: "flex",
        flexDirection: "column" as const,
        justifyContent: "space-between",
        gap: 12,
        flex: 1,
        minWidth: 180,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
      }}
      className={onClick ? 'hover:border-[#67E8F9] hover:scale-[1.01]' : ''}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, background: accent + "22",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17,
            flexShrink: 0,
          }}>{icon}</div>
          <span style={{ fontSize: 14, color: colors.text, fontWeight: 600, lineHeight: 1.3 }}>{label}</span>
        </div>
        <div style={{ fontSize: 30, fontWeight: 700, color: colors.text, lineHeight: 1.1, marginTop: 2 }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: colors.textMuted, whiteSpace: "nowrap" as const, overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>}
      </div>

      {onClick && (
        <div style={{ marginTop: "auto", paddingTop: 0, display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            style={{
              padding: "4px 11px",
              borderRadius: 8,
              border: `1px solid ${accent}55`,
              background: accent + "18",
              color: accent,
              fontSize: 11,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              cursor: "pointer",
              whiteSpace: "nowrap" as const,
              transition: "all 0.2s ease",
            }}
            className="hover:opacity-90 hover:brightness-110"
          >
            <span>View All</span>
            <span style={{ fontSize: 12, lineHeight: 1 }}>→</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Quick Actions (image 1 style) ───────────────────────────────────────────
const QUICK_ACTIONS = [
  {
    icon: "📋",
    label: "View All Assigned Scripts",
    desc: "Browse all rep scripts, completion & practices",
    accent: "#FACC15",
    page: "scripts",
  },
  {
    icon: "🎯",
    label: "View All Practice Sessions",
    desc: "Browse all pitches, scenarios, & digital twin practices",
    accent: "#A78BFA",
    page: "practice",
  },
  {
    icon: "📹",
    label: "View All Meetings & Recordings",
    desc: "Browse all rep meetings with filters & pagination",
    accent: "#67E8F9",
    page: "meetings",
  },
  {
    icon: "📋",
    label: "View All Activities",
    desc: "Track and review all team activities",
    accent: "#6C63FF",
    page: "activity",
  },
  {
    icon: "📈",
    label: "View Reps Performance",
    desc: "Analyze performance across all reps",
    accent: "#22C55E",
    page: "reps",
  },
  {
    icon: "🧠",
    label: "View Reps Skill Breakdown",
    desc: "Explore skill insights and competency gaps",
    accent: "#3B82F6",
    page: "skills",
  },
];

function QuickActions({
  setPage,
}: {
  setPage: (page: string) => void;
}) {
  const { isDark, colors } = useDashboardTheme();
  return (
    <div style={{
      background: colors.surface,
      border: `1px solid ${colors.border}`,
      borderRadius: 16,
      padding: "20px 24px",
      display: "flex",
      flexDirection: "column",
      height: "100%",
    }}>
      <div style={{
        fontSize: 14,
        fontWeight: 700,
        color: colors.text,
        letterSpacing: 1.2,
        textTransform: "uppercase" as const,
        marginBottom: 16,
      }}>
        Quick Actions
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: 12,
        flex: 1,
      }}>
        {QUICK_ACTIONS.map((action) => (
          <button
            key={action.label}
            onClick={() => setPage(action.page)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              background: colors.surfaceAlt,
              border: `1px solid ${colors.borderAlt}`,
              borderRadius: 12,
              padding: "12px 14px",
              color: colors.text,
              cursor: "pointer",
              textAlign: "left",
              width: "100%",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.25)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = colors.borderAlt;
              e.currentTarget.style.transform = "none";
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: action.accent + "22",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                flexShrink: 0,
              }}
            >
              {action.icon}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 14,
                fontWeight: 700,
                color: colors.text,
                marginBottom: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap" as const,
              }}>
                {action.label}
              </div>

              <div
                style={{
                  fontSize: 12,
                  color: colors.textMuted,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap" as const,
                }}
              >
                {action.desc}
              </div>
            </div>

            <span style={{ fontSize: 16, color: colors.textMuted, flexShrink: 0 }}>›</span>
          </button>
        ))}
      </div>
    </div>
  );
}


// ─── Main Component ───────────────────────────────────────────────────────────
export default function MeetingDashboard() {
  const { isDark, colors } = useDashboardTheme();
  const styles = getDynamicStyles(colors, isDark);
  const navigate = useNavigate();
  const location = useLocation();
  const locState = (location.state as any) || {};

  const [page, setPage] = useState<string>(() => locState.page || "dashboard");
  const [selectedRep, setSelectedRep] = useState<any | null>(() => locState.selectedRep || locState.rep || null);

  useEffect(() => {
    const st = (location.state as any) || {};
    if (st.page) setPage(st.page);
    if (st.selectedRep || st.rep) setSelectedRep(st.selectedRep || st.rep);
  }, [location.state]);

  const { data: tenantDashboard, isLoading: userLoading, isError: userError } = useTenantDashboard();
  const { rawMeetings, isLoading: meetingLoading, isError: meetingError } = useMeetingDashboardData();
  const effectiveTenantId = localStorage.getItem("tenantId") ?? "";
  const { data: scriptDashboardRes, isLoading: scriptLoading, isError: scriptError } = useScriptDashboardOverview(effectiveTenantId);
  const scriptOverview = scriptDashboardRes?.data;
  const loading = userLoading || meetingLoading || scriptLoading;
  const error = (userError || meetingError || scriptError) ? "Failed to load dashboard data." : null;
  const [isAdmin, setIsAdmin] = useState(false);
    const [isManager, setIsManager] = useState(false);
const [teamLoading, setTeamLoading] = useState(false);
  const [generatingTeamInsights, setGeneratingTeamInsights] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [teamData, setTeamData] = useState<any | null>(null);
  const [teamModalOpen, setTeamModalOpen] = useState(false);

  useEffect(() => {
    setIsAdmin(isAdminRole());
    setIsManager(isManagerRole());
}, []);

  const fetchTeamInsights = () => {
    setTeamLoading(true);
    setTeamError(null);
    return getTeamCoachingOverview()
      .then((res: any) => {
        setTeamData(res?.data ?? res);
        return res?.data ?? res;
      })
      .catch((err: any) => {
        setTeamError(err?.message || "Failed to fetch team overview.");
        throw err;
      })
      .finally(() => setTeamLoading(false));
  };

  const handleGenerateTeamInsights = () => {
    const readyCount = teamData?.insightsReady ?? 0;
    if (readyCount < 2) {
      setTeamError("At least 2 reps must have insights generated before team insights can be generated.");
      return;
    }

    setGeneratingTeamInsights(true);
    setTeamError(null);

    generateTeamCoachingInsights()
      .then(() => {
        return fetchTeamInsights();
      })
      .catch((err: any) => {
        setTeamError(err?.response?.data?.message || err?.message || "Failed to generate team insights.");
      })
      .finally(() => {
        setGeneratingTeamInsights(false);
      });
  };

  useEffect(() => {
    if (isAdmin || isManager) fetchTeamInsights();
  }, [isAdmin, isManager]);
  // const role = useAuthStore((state) => state.role);


  const metrics = useMemo(() => {
    if (!tenantDashboard) return null;
    return deriveMetrics(tenantDashboard, rawMeetings, scriptOverview);
  }, [tenantDashboard, rawMeetings, scriptOverview]);

  if (selectedRep) {
    return (
      <RepDashboardPage
        key={selectedRep.userId || selectedRep.id}
        rep={selectedRep}
        userData={tenantDashboard}
        rawMeetings={rawMeetings}
        meetingsLoading={meetingLoading}
        onBack={() => setSelectedRep(null)}
      />
    );
  }
  if (page === "reps") return (
    <AllRepsPage
      onBack={() => { setPage("dashboard"); setSelectedRep(null); }}
      userData={tenantDashboard}
      rawMeetings={rawMeetings}
      onRepClick={(rep: any) => setSelectedRep(rep)}
    />
  );
  if (page === "activity") return <AllActivityPage onBack={() => setPage("dashboard")} userData={tenantDashboard} />;
  if (page === "skills") return <SkillReportPage onBack={() => setPage("dashboard")} userData={tenantDashboard} />;
  if (page === "meetings") return <AllMeetingsPage onBack={() => setPage("dashboard")} userData={tenantDashboard} rawMeetings={rawMeetings} />;
  if (page === "practice") return <AllPracticeSessionsPage onBack={() => setPage("dashboard")} userData={tenantDashboard} rawMeetings={rawMeetings} />;
  if (page === "scripts") return <AllScriptsPage onBack={() => setPage("dashboard")} userData={tenantDashboard} scriptOverview={scriptOverview} />;

  return (
    <div style={styles.root}>
      <main style={styles.main}>

        {/* ── Header ── */}


        {loading && (
          <div style={{ textAlign: "center", padding: 80, color: colors.textMuted, fontSize: 14 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⟳</div>Loading dashboard data...
          </div>
        )}
        {error && <div style={{ textAlign: "center", padding: 80, color: "#F87171", fontSize: 14 }}>{error}</div>}

        {!loading && metrics && (
          <>
            {/* ── Row 1: KPI Cards ── */}
            <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" as const }}>
              {/* Team Readiness ring */}
              <div style={{
                background: isDark ? "linear-gradient(135deg, rgba(108,99,255,0.15) 0%, rgba(108,99,255,0.05) 100%)" : "linear-gradient(135deg, rgba(108,99,255,0.08) 0%, rgba(108,99,255,0.02) 100%)",
                border: isDark ? "1px solid rgba(108,99,255,0.3)" : "1px solid rgba(108,99,255,0.15)", borderRadius: 16, padding: "20px 28px",
                display: "flex", flexDirection: "column" as const, alignItems: "center", gap: 10, minWidth: 160,
              }}>
                <ScoreRing score={metrics.teamReadiness} size={80} />
                <div style={{ textAlign: "center" as const }}>
                  <div style={{ fontSize: 13, color: colors.text, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase" as const }}>Team Readiness</div>
                  <div style={{ fontSize: 14, color: "#4ADE80", marginTop: 2 }}>↑ vs last 30 days</div>
                </div>
              </div>
              <TopCard icon="🎯" label="Total Practice Sessions" value={metrics.practiceSessions} accent="#A78BFA" sub={`${tenantDashboard?.data?.totalPitchCount || 0} pitches · ${tenantDashboard?.data?.totalScenarioCount || 0} scenarios`} onClick={() => setPage("practice")} />
              <TopCard icon="📹" label="Meetings Reviewed" value={metrics.meetingsReviewed} accent="#67E8F9" sub={`${metrics.pendingMeetings} pending analysis`} onClick={() => setPage("meetings")} />
              <TopCard icon="📋" label="Scripts Assigned & Practiced" value={`${metrics.scriptsCompleted} / ${metrics.scriptAssigned}`} accent="#FACC15" sub={`${metrics.scriptPracticeCount} practices · ${metrics.scriptsPct}% rate`} onClick={() => setPage("scripts")} />
              <TopCard icon="👥" label="Active Users" value={`${metrics.activeUsers}/${metrics.totalUsers}`} accent="#4ADE80" sub="Active team members" />
            </div>

            {/* Pending Alert */}
            {/* {metrics.pendingMeetings > 0 && (
              <div style={{
                background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.25)",
                borderRadius: 12, padding: "12px 18px", marginBottom: 20,
                display: "flex", alignItems: "center", gap: 10,
              }}>
                <span style={{ fontSize: 16 }}>⚠</span>
                <span style={{ fontSize: 13, color: "rgba(255,255,255,0.85)" }}>
                  <strong style={{ color: "#FACC15" }}>{metrics.pendingMeetings} meetings</strong> are pending AI analysis
                </span>
              </div>
            )} */}
            <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 16, marginBottom: 20, alignItems: "stretch" }}>

              {/* Recent Team Activity */}
              <div style={{ ...styles.section, display: "flex", flexDirection: "column", height: "100%" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <span style={styles.sectionTitle}>Recent Team Activity</span>
                  <button style={styles.viewAll} onClick={() => setPage("activity")}>View all activity →</button>
                </div>

                {/* Table header */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "1.8fr 1.2fr 1.4fr 90px 100px 44px",
                  gap: 12, padding: "0 6px 10px",
                  borderBottom: `1px solid ${colors.divider}`,
                }}>
                  {["Rep", "Activity", "Details", "Score", "Date", ""].map((h) => (
                    <span key={h} style={{ fontSize: 14, color: colors.text, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" as const }}>{h}</span>
                  ))}
                </div>

                {/* Table rows (scrollable) */}
                <div style={{ flex: 1, maxHeight: 300, overflowY: "auto" }}>
                  {metrics.recentActivity.map((item: any, idx: number) => (
                    <div key={item.id || item.sessionId || `activity-${idx}`} style={{
                      display: "grid",
                      gridTemplateColumns: "1.8fr 1.2fr 1.4fr 90px 100px 44px",
                      gap: 12, padding: "12px 6px",
                      borderBottom: `1px solid ${colors.borderAlt}`,
                      alignItems: "center",
                      transition: "background 0.15s",
                      cursor: item.id ? "pointer" : "default",
                    }}
                      onClick={() => {
                        if (item.isScript) {
                          navigate(`/script-details/${item.id}?assignedToId=${item.raw?.repId}`, { state: { from: location.pathname + location.search, page: page, selectedRep: selectedRep } });
                        } else if (item.id) {
                          navigate(`/meeting-detail/${item.id}`, { state: { meeting: item.raw, meetingId: item.id, from: location.pathname + location.search, page: page, selectedRep: selectedRep } });
                        }
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = colors.rowHover)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      {/* Rep */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        <Avatar name={item.rep} size={32} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: colors.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>{item.rep}</span>
                      </div>

                      {/* Activity badge */}
                      <div><Badge type={item.type} /></div>

                      {/* Details */}
                      <span style={{ fontSize: 14, color: colors.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const, textTransform: "uppercase" }}>{item.details}</span>

                      {/* Score pill */}
                      <div>
                        {item.score > 0 ? (
                          <span style={{
                            fontSize: 14, fontWeight: 700,
                            color: getScoreColor(item.score),
                            background: item.score >= 90 ? "rgba(74,222,128,0.1)" : item.score >= 70 ? "rgba(163,230,53,0.12)" : item.score >= 50 ? "rgba(250,204,21,0.1)" : "rgba(248,113,113,0.1)",
                            padding: "3px 10px", borderRadius: 20, display: "inline-block",
                          }}>{Math.round(item.score)}</span>
                        ) : (
                          <span style={{ fontSize: 13, color: colors.textMuted }}>—</span>
                        )}
                      </div>

                      {/* Date */}
                      <span style={{ fontSize: 14, color: colors.text }}>{fmtDate(item.date)}</span>

                      {/* Arrow */}
                      <ArrowBtn onClick={() => {
                        if (item.isScript) {
                          navigate(`/script-details/${item.id}?assignedToId=${item.raw?.repId}`, { state: { from: location.pathname + location.search, page: page, selectedRep: selectedRep } });
                        } else if (item.id) {
                          navigate(`/meeting-detail/${item.id}`, { state: { meeting: item.raw, meetingId: item.id, from: location.pathname + location.search, page: page, selectedRep: selectedRep } });
                        } else {
                          setPage("activity");
                        }
                      }} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Team Skill Breakdown */}
              <div style={{ ...styles.section, display: "flex", flexDirection: "column", height: "100%" }}>
                <div style={{ marginBottom: 18 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <span style={styles.sectionTitle}>Team Skill Breakdown</span>
                    <button style={styles.viewAll} onClick={() => setPage("skills")}>
                      View full skill report →
                    </button>
                  </div>
                  {/* Column headers */}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: colors.textMuted, letterSpacing: 0.6, minWidth: 142 }}>SKILL</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: colors.textMuted, letterSpacing: 0.6, minWidth: 24, textAlign: "right" as const }}>
                      TEAM AVERAGE
                    </span>
                  </div>
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column" as const, justifyContent: "space-between", gap: 12 }}>
                  {[
                    { label: "Discovery", key: "Discovery", icon: "🔍" },
                    { label: "Objection Handling", key: "Objection Handling", icon: "🛡" },
                    { label: "Product Knowledge", key: "Product Knowledge", icon: "📖" },
                    { label: "Closing", key: "Closing", icon: "🤝" },
                  ].map(({ label, key, icon }) => (
                    <SkillBar key={key} label={label} value={(metrics.skills as any)[key] || 0} icon={icon} />
                  ))}
                </div>

              </div>

            </div>

            {/* ── Row 3: Team Performance | Quick Actions ── */}
            <div style={{
              display: "grid",
              gridTemplateColumns: " 1.4fr 1fr",
              gap: 16,
            }}>

              {/* Team Performance (By Rep) */}
              <div style={styles.section}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <span style={styles.sectionTitle}>Team Performance (By Rep)</span>
                  <button style={styles.viewAll} onClick={() => setPage("reps")}>View all reps →</button>
                </div>

                {/* Col headers */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 112px 140px 36px", gap: 8, padding: "0 0 8px", borderBottom: `1px solid ${colors.divider}`, marginBottom: 2 }}>
                  {["REP", "SCORE", "ACTIVITY", ""].map((h) => (
                    <span key={h} style={{ fontSize: 14, color: colors.text, fontWeight: 700, letterSpacing: 0.6 }}>{h}</span>
                  ))}
                </div>

                {/* Scrollable Table rows */}
                <div style={{ maxHeight: 260, overflowY: "auto" }}>
                  {metrics.reps.map((rep: any, idx: number) => (
                    <div key={rep.userId || rep.id || `rep-${idx}`}
                      onClick={() => setSelectedRep(rep)}
                      style={{
                        display: "grid", gridTemplateColumns: "1fr 112px 140px 36px",
                        gap: 8, padding: "10px 0", borderBottom: `1px solid ${colors.borderAlt}`, alignItems: "center",
                        cursor: "pointer",
                      }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        <Avatar name={rep.name} size={30} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: colors.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>{rep.name}</div>
                          <div style={{ fontSize: 14, color: colors.textMuted }}>{rep.role}</div>
                        </div>
                      </div>
                      <ScoreRing score={rep.avgScore} size={44} />
                      {/* Mini bar chart */}
                      <div style={{ display: "flex", gap: 3, alignItems: "flex-end", height: 28 }}>
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} style={{
                            width: 10, borderRadius: 3,
                            height: `${(5 - i) * 20}%`,
                            minHeight: 5,
                            background: i < (rep.practiceSessionCount || 0) ? "#6C63FF" : colors.divider,
                            transition: "height 0.4s ease",
                          }} />
                        ))}
                      </div>
                      <ArrowBtn onClick={() => setSelectedRep(rep)} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Actions */}
              {!(isAdmin || isManager) && (

                <QuickActions setPage={setPage} />)}

              {(isAdmin || isManager) && (
                <TeamCoachingInsightsCard
                  data={teamData}
                  loading={teamLoading}
                  generating={generatingTeamInsights}
                  error={teamError}
                  onViewMore={() => setTeamModalOpen(true)}
                  // onRefresh={fetchTeamInsights}
                  onGenerate={handleGenerateTeamInsights}
                />
              )}
            </div>

            {(isAdmin || isManager) && (
              <TeamCoachingModal
                open={teamModalOpen}
                onClose={() => setTeamModalOpen(false)}
                loading={teamLoading}
                generating={generatingTeamInsights}
                error={teamError}
                data={teamData}
                onGenerate={handleGenerateTeamInsights}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
}

// ─── Exported Types & Helpers ─────────────────────────────────────────────────
export type RawMeeting = {
  id: string; meetingType: string; status: string;
  overallPercentage: number; score: number; createdAt: string;
  durationMinutes?: number; talkRatio?: { agent: number; customer: number };
  ai_score?: any;
  ai_summary?: { summary?: string[]; insights?: string[]; suggestions?: string[] };
  summary?: string[]; insights?: string[]; suggestions?: string[];
  companyName?: string;
  customerData?: { companyName?: string; buyerPersona?: string[] };
  customerContact?: { firstName?: string; lastName?: string; email?: string };
  tenantId?: string;
  TargetCompanyProfile?: {
    id?: string; companyName?: string; industry?: string;
    businessModel?: string; decisionProcess?: string; techStack?: string;
    competitorsInvolved?: string; currentVendors?: string | null;
  } | null;
  User?: { id: string; name: string; role?: string; userImg?: string | null };
  user?: { id: string; name: string; role?: string; userImg?: string | null };
  createdBy?: string;
  otherDetails?: {
    meetingType?: string; meetingPurpose?: string[];
    meetingObjective?: string[]; buyerGoals?: string; knownPainPoints?: string;
  };
  uploaded_audio_url?: string | null; s3Url?: string | null;
  userId?: string;
  pitchTitle?: string;
};

export type TenantUser = {
  id: string; name?: string; email?: string;
  tenant?: { id?: string; name?: string };
};

export type UserGroup = {
  userId: string; userName: string; userRole: "admin" | "manager" | "user";
  companies: string[]; company: string; customer: string;
  industry: string; businessModel: string; decisionProcess: string;
  techStack: string; competitorsInvolved: string;
  meetings: RawMeeting[]; totalMeetings: number; avgScore: number;
  cold: number; warm: number; inboundLead: number;
  existingRelationship: number; currentCustomer: number; reEngagement: number;
  recorded: number; activeCount: number; completedCount: number;
  allSummaries: string[]; allInsights: string[]; allSuggestions: string[]; allJustifications: string[];
  categories: any[]; latestMeetingDate: string;
  meetingPurposes: string[]; meetingObjectives: string[];
  talkRatio: { agent: number; customer: number };
};

const VALID_PURPOSES = ["DISCOVERY", "ROI_DISCUSSION", "OBJECTION_HANDLING", "REVIEW", "INTRODUCTION"];
const VALID_OBJECTIVES = ["FOLLOW_UP_CALL", "PROPOSAL", "DEMO"];

export const isRecordedMeeting = (m: RawMeeting): boolean => {
  const rawType = String(m.otherDetails?.meetingType || m.meetingType || '').trim().toLowerCase();
  if (rawType && rawType !== 'recorded') return false;
  return !m.TargetCompanyProfile || !m.TargetCompanyProfile.id;
};

const buildCategories = (ai_score: any) => {
  if (!ai_score) return [];
  const sections = [
    { key: "opening", label: "Opening", color: "#a78bfa", src: ai_score.opening },
    { key: "discovery", label: "Discovery", color: "#38bdf8", src: ai_score.discovery },
    { key: "pitch", label: "Pitch Delivery", color: "#34d399", src: ai_score.pitch_delivery },
    { key: "engagement", label: "Engagement", color: "#f59e0b", src: ai_score.engagement },
    { key: "confidence", label: "Confidence & Tone", color: "#fb923c", src: ai_score.confidence_and_tone },
    { key: "closing", label: "Closing", color: "#f472b6", src: ai_score.closing },
    { key: "impression", label: "Overall Impression", color: "#60a5fa", src: ai_score.overall_impression },
  ];
  return sections
    .filter((s) => s.src && typeof s.src === "object")
    .map((s) => ({
      key: s.key, label: s.label, color: s.color,
      metrics: Object.entries(s.src)
        .filter(([, v]) => typeof v === "number")
        .map(([k, v]) => ({
          label: k.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()),
          score: Number(v), max: 3,
        })),
    }))
    .filter((c) => c.metrics.length > 0);
};

const mergeCategories = (allCats: any[][]): any[] => {
  const map: Record<string, any> = {};
  for (const cats of allCats) {
    for (const cat of cats) {
      if (!map[cat.key]) map[cat.key] = { label: cat.label, color: cat.color, metricSums: {} };
      for (const m of cat.metrics) {
        if (!map[cat.key].metricSums[m.label]) map[cat.key].metricSums[m.label] = { sum: 0, max: m.max, count: 0 };
        map[cat.key].metricSums[m.label].sum += m.score;
        map[cat.key].metricSums[m.label].count += 1;
      }
    }
  }
  return Object.entries(map).map(([key, val]: [string, any]) => ({
    key, label: val.label, color: val.color,
    metrics: Object.entries(val.metricSums).map(([label, ms]: [string, any]) => ({
      label, score: ms.count > 0 ? Math.round(ms.sum / ms.count) : 0, max: ms.max,
    })),
  }));
};

const normalizeMeetingType = (value?: string): string => {
  const raw = String(value || "").trim().toLowerCase().replace(/_/g, " ").replace(/\s+/g, " ");
  const mapping: Record<string, string> = {
    cold: "cold", warm: "warm",
    "inbound lead": "inbound lead", "inboundlead": "inbound lead",
    "existing relationship": "existing relationship", "existingrelationship": "existing relationship",
    "current customer": "current customer", "currentcustomer": "current customer",
    "re-engagement": "re-engagement", "reengagement": "re-engagement", "re engagement": "re-engagement",
    call: "cold", demo: "cold", negotiation: "cold", objection: "cold",
  };
  return mapping[raw] || raw;
};

export const getMeetingType = (raw: RawMeeting): string =>
  normalizeMeetingType(raw.meetingType || raw.otherDetails?.meetingType || "cold");

export const normalizeStatus = (s: string): string => {
  const m: Record<string, string> = { active: "Active", processing: "Processing", completed: "Completed", stalled: "Stalled" };
  return m[s?.toLowerCase()] || "Active";
};

const capScore = (s: number) => Math.min(Math.round(s), 100);

export const groupByUser = (rawList: RawMeeting[]): UserGroup[] => {
  const map: Record<string, { user: { id: string; name: string; role: string }; meetings: RawMeeting[] }> = {};
  for (const m of rawList) {
    const userObj = m.User || m.user;
    const uid = userObj?.id || String(m.createdBy) || "unknown";
    const name = userObj?.name || "Unknown";
    const role = (userObj?.role || "user").toLowerCase();
    if (!map[uid]) map[uid] = { user: { id: uid, name, role }, meetings: [] };
    map[uid].meetings.push(m);
  }
  return Object.values(map).map(({ user, meetings }) => {
    const scores = meetings.map((m) => capScore(m.overallPercentage || m.score || 0));
    const avgScore = scores.length ? capScore(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
    const types = meetings.map(getMeetingType);
    const statuses = meetings.map((m) => normalizeStatus(m.status));
    const ratioMeetings = meetings.filter((m) => m.talkRatio && typeof m.talkRatio.agent === "number");
    const avgAgent = ratioMeetings.length
      ? Math.round(ratioMeetings.reduce((s, m) => s + (m.talkRatio!.agent || 0), 0) / ratioMeetings.length) : 60;
    const allSummaries = meetings.flatMap((m) => m.ai_summary?.summary || m.summary || []).filter(Boolean);
    const allInsights = meetings.flatMap((m) => m.ai_summary?.insights || m.insights || []).filter(Boolean);
    const allSuggestions = meetings.flatMap((m) => m.ai_summary?.suggestions || m.suggestions || []).filter(Boolean);
    const allJustifications = meetings.flatMap((m) => (m.ai_score as any)?.justification || []).filter(Boolean);
    const categories = mergeCategories(meetings.map((m) => buildCategories(m.ai_score || {})));
    const dates = meetings.map((m) => m.createdAt).filter(Boolean).sort().reverse();
    const companySet = new Set<string>();
    meetings.forEach((m) => { const c = m.TargetCompanyProfile?.companyName || m.customerData?.companyName || m.companyName || ""; if (c) companySet.add(c); });
    const companies = [...companySet].sort();
    const company = companies[0] || "";
    const customer = meetings[0]?.customerData?.companyName || meetings[0]?.companyName || "";
    const profileMeeting = meetings.find((m) => !isRecordedMeeting(m) && m.TargetCompanyProfile?.companyName);
    const profile = profileMeeting?.TargetCompanyProfile || {};
    const rawPurposes = [...new Set(meetings.flatMap((m) => m.otherDetails?.meetingPurpose || []))];
    const rawObjectives = [...new Set(meetings.flatMap((m) => m.otherDetails?.meetingObjective || []))];
    const meetingPurposes = rawPurposes.filter((p) => VALID_PURPOSES.includes(p.toUpperCase()));
    const meetingObjectives = rawObjectives.filter((o) => VALID_OBJECTIVES.includes(o.toUpperCase()));
    return {
      userId: user.id, userName: user.name, userRole: user.role as "admin" | "manager" | "user",
      companies, company: (profile as any).companyName || company, customer,
      industry: (profile as any).industry || "", businessModel: (profile as any).businessModel || "",
      decisionProcess: (profile as any).decisionProcess || "", techStack: (profile as any).techStack || "",
      competitorsInvolved: (profile as any).competitorsInvolved || "",
      meetings, totalMeetings: meetings.length, avgScore,
      cold: types.filter((t) => t === "cold").length,
      warm: types.filter((t) => t === "warm").length,
      inboundLead: types.filter((t) => t === "inbound lead").length,
      existingRelationship: types.filter((t) => t === "existing relationship").length,
      currentCustomer: types.filter((t) => t === "current customer").length,
      reEngagement: types.filter((t) => t === "re-engagement").length,
      recorded: meetings.filter(isRecordedMeeting).length,
      activeCount: statuses.filter((s) => s === "Active").length,
      completedCount: statuses.filter((s) => s === "Completed").length,
      allSummaries, allInsights, allSuggestions, allJustifications, categories,
      latestMeetingDate: dates[0] || "", meetingPurposes, meetingObjectives,
      talkRatio: { agent: avgAgent, customer: 100 - avgAgent },
    };
  });
};



